import type { PortalDashboard } from '@erp/shared';
import { FileText, Store, Wallet } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Badge, Button as ShButton } from '../../components/shadcn/index.js';
import { Card, DataTable, type DataTableColumn, EmptyState, StatCard } from '../../components/ui/index.js';
import { customerApi } from '../../lib/customerApi.js';
import { formatMoney } from '../../lib/money.js';
import { useSiteContextStore } from '../../lib/siteContextStore.js';

type Activity = PortalDashboard['recent'][number];

export function PortalDashboardPage(): JSX.Element {
  const { t, i18n } = useTranslation('portal');
  const [data, setData] = useState<PortalDashboard | null>(null);
  const [error, setError] = useState(false);
  const storeSlug = useSiteContextStore((s) => s.tenant?.slug ?? null);
  const storeHref = storeSlug ? `/s/${storeSlug}` : null;

  useEffect(() => {
    void customerApi()
      .get<PortalDashboard>('/dashboard')
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, []);

  const columns: DataTableColumn<Activity>[] = useMemo(
    () => [
      {
        id: 'date',
        header: t('columns.date'),
        sortValue: (r) => r.date,
        cell: (r) => r.date,
      },
      {
        id: 'type',
        header: t('columns.type'),
        sortValue: (r) => r.type,
        cell: (r) => <Badge variant="secondary">{t(`activity.${r.type}`)}</Badge>,
      },
      {
        id: 'reference',
        header: t('columns.reference'),
        sortValue: (r) => r.reference,
        cell: (r) => <span className="font-mono text-xs">{r.reference}</span>,
      },
      {
        id: 'amount',
        header: t('columns.amount'),
        align: 'end',
        cellClassName: 'font-mono tabular-nums',
        sortValue: (r) => Number(r.amount),
        cell: (r) => formatMoney(r.amount, { locale: i18n.language, currency: r.currencyCode }),
      },
    ],
    [t, i18n.language],
  );

  const loading = !data && !error;

  return (
    <div className="flex flex-col gap-5 pb-16 lg:pb-0">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('dashboard.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('dashboard.subtitle')}</p>
        </div>
        {/* The dashboard is where a customer lands after signing in, so carrying on
            shopping has to be reachable from here and not only from the chrome. */}
        {storeHref && (
          <ShButton asChild variant="outline" size="sm" className="gap-1.5">
            <Link to={storeHref}>
              <Store className="h-4 w-4" aria-hidden />
              {t('backToStore', { defaultValue: 'Back to the shop' })}
            </Link>
          </ShButton>
        )}
      </header>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          label={t('dashboard.openInvoices')}
          value={data?.openInvoiceCount ?? 0}
          loading={loading}
          icon={FileText}
          tone="info"
        />
        {(data?.totals ?? []).map((tot) => (
          <StatCard
            key={tot.currencyCode}
            label={`${t('dashboard.outstanding')} · ${tot.currencyCode}`}
            value={formatMoney(tot.outstanding, { locale: i18n.language, currency: tot.currencyCode })}
            loading={loading}
            icon={Wallet}
            tone={Number(tot.outstanding) > 0 ? 'warning' : 'success'}
            hint={`${t('dashboard.sales')}: ${formatMoney(tot.invoiced, { locale: i18n.language, currency: tot.currencyCode })} · ${t('dashboard.paid')}: ${formatMoney(tot.paid, { locale: i18n.language, currency: tot.currencyCode })}`}
          />
        ))}
        {!loading && (data?.totals ?? []).length === 0 && (
          <StatCard label={t('dashboard.outstanding')} value="0" tone="success" />
        )}
      </div>

      <section>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-fg-muted">
          {t('dashboard.recent')}
        </h2>
        {error ? (
          <Card className="p-6 text-sm text-danger">{t('loadError')}</Card>
        ) : (data?.recent ?? []).length === 0 && !loading ? (
          <EmptyState
            icon={<FileText className="h-8 w-8" aria-hidden />}
            title={t('dashboard.empty')}
            description={t('dashboard.emptyDescription')}
          />
        ) : (
          <Card className="overflow-hidden p-0">
            <DataTable
              columns={columns}
              data={data?.recent ?? []}
              rowKey={(r) => `${r.type}:${r.reference}:${r.date}`}
              searchPlaceholder={t('dashboard.searchPlaceholder')}
            />
          </Card>
        )}
      </section>
    </div>
  );
}
